import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import getMenu from 'api/Header/getMenu';
import getFooter from 'api/Footer/getFooter';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import { setHeadData } from 'utils/metaDataUtils/headData';
import { setPianoConfig } from 'services/Piano';
import getCompanyBySlug from 'api/Company/getCompanyBySlug';
import type { PageAdConfig } from 'interfaces/ads/Ad';
import type { PageViewDataLayerObject } from 'services/Gtm/entities';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import setCompanyPageAdConfig from 'services/Ad/config/setCompanyPageAdConfig';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import CompanyEarningsReport, { type CompanyQuarterlyEarningsProps } from 'pages/CompanyEarningsReport';
import NotFoundError from 'interfaces/errors/NotFoundError';
import getReportBySlug from 'api/EarningsReport/getReportBySlug';
import getTag from 'api/Tag/getTag';
import { PageType } from 'interfaces/content/articles/Post';
import logServerError from 'utils/log/logServerError';

const CompanyQuarterlyEarnings: NextPage<CompanyQuarterlyEarningsProps> = ({
  company,
  earningsReport,
  latestPosts,
}: CompanyQuarterlyEarningsProps) => (
  <CompanyEarningsReport
    company={company}
    earningsReport={earningsReport}
    latestPosts={latestPosts}
    subdomain='own'
  />
);

let quartrAssetsUrl = process?.env?.SITE_BASE_URL || '';
if (quartrAssetsUrl.includes('localhost')) {
  quartrAssetsUrl = 'https://develop.fortune.com';
}

export default CompanyQuarterlyEarnings;

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    if (!process.env.FEATURE_FLAG_QUARTR) {
      throw new NotFoundError(`Page not available`);
    }

    const { slug, quarter } = context.params as { slug: string; quarter: string };
    const menu = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const { company } = await getCompanyBySlug(slug);
    const { earningsReport } = await getReportBySlug(slug, quarter);

    const title = `${company.title} ${earningsReport.title} Earnings Call Transcript`;

    const headData = safe(
      () =>
        setHeadData({
          ...company,
          image: `${quartrAssetsUrl}/company-assets/f-500-quarterly-earnings-call-transcript.png`,
          link: earningsReport.permalink,
          metaTitle: title,
          title: `${title} | Fortune`,
        }),
      fallbackData.headData,
    );

    const dataLayer: PageViewDataLayerObject = safe(
      () =>
        setPageGtmConfig({
          ...company,
          pageType: PageType.EARNINGS_REPORT,
        }),
      fallbackData.dataLayer,
    );
    const pageAdConfig: PageAdConfig = safe(() => setCompanyPageAdConfig(company), fallbackData.pageAdConfig);
    const permutivePageConfig: ArcSpanPageConfig = safe(
      () => setarcSpanPageConfig(company),
      fallbackData.arcSpanPageConfig,
    );
    const pianoPageConfig: PianoPageConfig = safe(
      () => setPianoConfig(company, company.hasPaywall),
      fallbackData.pianoPageConfig,
    );

    const latestPosts = {
      posts: [],
    };

    try {
      const tag = await getTag('fortune-500', 6, 0);
      latestPosts.posts = tag.posts;
    } catch (error) {
      logServerError(error as Error);
    }

    return {
      props: {
        company,
        dataLayer,
        earningsReport,
        footer,
        headData,
        headerNav: menu,
        isCompanyPage: true,
        latestPosts,
        pageAdConfig,
        permutivePageConfig,
        pianoPageConfig,
      },
      revalidate: 60,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths<{ slug: string }> = () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [],
    };
  }

  return {
    fallback: 'blocking',
    paths: [
      {
        params: { quarter: 'Q1', slug: 'walmart' },
      },
    ],
  };
};
